import React, { useRef, useEffect, forwardRef, useImperativeHandle, useState, useMemo } from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useGantt } from '../context/GanttContext';
import TimelineHeader from './TimelineHeader';
import TaskBar from './TaskBar';
import DependencyLine from './DependencyLine';
import BaselineBar from './BaselineBar';
import { getTodayPosition, getGridLinePositions, getWeekendBands } from '../utils/dateUtils';
import { isCriticalDependency } from '../utils/criticalPathUtils';
import { FlattenedTask, ROW_HEIGHT, HEADER_HEIGHT } from '../types/gantt.types';

export interface TimelinePanelRef {
  scrollTo: (scrollTop: number) => void;
}

interface TimelinePanelProps {
  onScroll?: (scrollTop: number) => void;
  onTaskClick?: (task: FlattenedTask) => void;
  height?: number;
}

const TimelinePanel = forwardRef<TimelinePanelRef, TimelinePanelProps>(
  ({ onScroll, onTaskClick, height = 400 }, ref) => {
    const {
      tasks,
      dependencies,
      timelineConfig,
      showCriticalPath,
      showBaselines,
      selectedBaselines,
      baselines,
      criticalTaskIds,
      baselineLoading,
    } = useGantt();

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [scrollLeft, setScrollLeft] = useState(0);

    useImperativeHandle(ref, () => ({
      scrollTo: (scrollTop: number) => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollTop;
        }
      },
    }));

    // Handle scroll events
    useEffect(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const handleScroll = () => {
        setScrollLeft(container.scrollLeft);
        if (onScroll) {
          onScroll(container.scrollTop);
        }
      };

      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => container.removeEventListener('scroll', handleScroll);
    }, [onScroll]);

    const totalWidth = timelineConfig.totalDays * timelineConfig.dayWidth;
    const totalHeight = tasks.length * ROW_HEIGHT;
    const todayX = getTodayPosition(timelineConfig);

    // Both track the visible timescale, so zooming out to quarters/years costs
    // a handful of elements instead of one per day.
    const gridLines = useMemo(
      () => getGridLinePositions(timelineConfig),
      [timelineConfig]
    );
    const weekendBands = useMemo(
      () => getWeekendBands(timelineConfig),
      [timelineConfig]
    );

    // Create task lookup maps for dependencies
    // Dependencies typically connect leaf tasks, so we need to prioritize them
    const taskMapByLevel = {
      phase: new Map<number, FlattenedTask>(),
      activity: new Map<number, FlattenedTask>(),
      task: new Map<number, FlattenedTask>(),
    };

    tasks.forEach((task) => {
      taskMapByLevel[task.level].set(task.id, task);
    });

    // Helper to find task by ID, prioritizing leaf tasks
    const findTaskById = (id: number): FlattenedTask | undefined => {
      // First try to find as a task (leaf level)
      if (taskMapByLevel.task.has(id)) return taskMapByLevel.task.get(id);
      // Then try activity
      if (taskMapByLevel.activity.has(id)) return taskMapByLevel.activity.get(id);
      // Finally try phase
      if (taskMapByLevel.phase.has(id)) return taskMapByLevel.phase.get(id);
      return undefined;
    };

    // Get baseline configs for selected baselines
    const selectedBaselineConfigs = baselines.filter((b) =>
      selectedBaselines.includes(b.id)
    );

    // Ensure minimum dimensions for scroll to work
    const svgWidth = Math.max(totalWidth, 1000);
    const svgHeight = Math.max(totalHeight, 500);

    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Baseline loading overlay */}
        {baselineLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.7)',
              zIndex: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
              tip="Loading baselines..."
            />
          </div>
        )}

        {/* Fixed Timeline Header */}
        <div style={{ flexShrink: 0 }}>
          <TimelineHeader scrollLeft={scrollLeft} />
        </div>

        {/* Scrollable Timeline Body */}
        <div
          ref={scrollContainerRef}
          style={{
            flex: 1,
            overflow: 'auto',
            position: 'relative',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {/* Inner container with actual dimensions */}
          <div
            style={{
              width: svgWidth,
              height: svgHeight,
              position: 'relative',
            }}
          >
            <svg
              width={svgWidth}
              height={svgHeight}
              style={{
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            >
              {/* Background grid lines */}
              <g className="grid-lines">
                {/* Vertical grid lines — one per timescale column */}
                {gridLines.map((x, index) => (
                  <line
                    key={`vgrid-${index}`}
                    x1={x}
                    y1={0}
                    x2={x}
                    y2={svgHeight}
                    stroke="#f0f0f0"
                    strokeWidth={1}
                  />
                ))}

                {/* Horizontal grid lines (rows) */}
                {tasks.map((_, index) => (
                  <line
                    key={`hgrid-${index}`}
                    x1={0}
                    y1={(index + 1) * ROW_HEIGHT}
                    x2={svgWidth}
                    y2={(index + 1) * ROW_HEIGHT}
                    stroke="#f0f0f0"
                    strokeWidth={1}
                  />
                ))}
              </g>

              {/* Weekend shading — dropped once a day is too narrow to read */}
              <g className="weekend-shading">
                {weekendBands.map((band, index) => (
                  <rect
                    key={`weekend-${index}`}
                    x={band.x}
                    y={0}
                    width={band.width}
                    height={svgHeight}
                    fill="rgba(0,0,0,0.02)"
                  />
                ))}
              </g>

              {/* Today marker */}
              {todayX !== null && (
                <g className="today-marker">
                  <line
                    x1={todayX}
                    y1={0}
                    x2={todayX}
                    y2={svgHeight}
                    stroke="#1890ff"
                    strokeWidth={2}
                    strokeDasharray="4,4"
                  />
                  <circle cx={todayX} cy={8} r={4} fill="#1890ff" />
                </g>
              )}

              {/* Task bars */}
              <g className="task-bars">
                {tasks.map((task) => (
                  <TaskBar
                    key={task.key}
                    task={task}
                    config={timelineConfig}
                    isCritical={showCriticalPath && criticalTaskIds.has(task.id)}
                    onClick={onTaskClick}
                  />
                ))}
              </g>

              {/* Baseline bars - rendered after task bars so they appear on top */}
              {showBaselines && selectedBaselineConfigs.length > 0 && (
                <g className="baseline-bars">
                  {tasks.flatMap((task) => {
                    const taskBaselines = task.baselines || [];
                    if (taskBaselines.length === 0) return [];

                    // Define fallback colors matching BaselineColors
                    const fallbackColors = ['#faad14', '#722ed1', '#13c2c2', '#eb2f96', '#52c41a'];

                    return taskBaselines
                      .filter((b) => selectedBaselines.includes(b.baselineId))
                      .map((baseline, bIndex) => {
                        const config = selectedBaselineConfigs.find(
                          (c) => c.id === baseline.baselineId
                        );
                        // Use config color, or fallback based on baseline index
                        const baselineColor = config?.color || fallbackColors[bIndex % fallbackColors.length];

                        return (
                          <BaselineBar
                            key={`baseline-${task.id}-${baseline.baselineId}`}
                            baseline={baseline}
                            task={task}
                            config={timelineConfig}
                            color={baselineColor}
                            index={bIndex}
                          />
                        );
                      });
                  })}
                </g>
              )}

              {/* Dependency lines */}
              <g className="dependency-lines">
                {dependencies.map((dep, index) => {
                  // Find tasks by ID, prioritizing leaf tasks
                  const fromTask = findTaskById(dep.from);
                  const toTask = findTaskById(dep.to);

                  // Skip if either task is not visible (collapsed or not in view)
                  if (!fromTask || !toTask) return null;

                  // Ensure both tasks have valid positions
                  if (typeof fromTask.rowIndex !== 'number' || typeof toTask.rowIndex !== 'number') {
                    return null;
                  }

                  return (
                    <DependencyLine
                      key={`dep-${index}`}
                      dependency={dep}
                      fromTask={fromTask}
                      toTask={toTask}
                      config={timelineConfig}
                      isCritical={
                        showCriticalPath && isCriticalDependency(dep, criticalTaskIds)
                      }
                    />
                  );
                })}
              </g>
            </svg>
          </div>
        </div>
      </div>
    );
  }
);

TimelinePanel.displayName = 'TimelinePanel';

export default TimelinePanel;
